// =============================================================================
// features/configuracoes/ConfiguracoesPage.tsx — Hub de configurações.
//
// Estrutura:
//   - Camada 1 (Conta): Perfil, Segurança e Aparência funcionais (F8-S09).
//     Conteúdo em ContaSection.tsx.
//   - Camada 2 (Administração): cards gated por permissão, divididos em dois grupos:
//       · Gestão         — Produtos & Regras, Cidades, Agentes, Follow-up,
//                          Cobrança (3 cards), Templates WhatsApp, Agente de IA
//       · Adm. técnica   — Usuários & Papéis, Feature Flags
//     Grupos e camada omitidos quando sem permissão; sem nenhum card permitido, a
//     própria aba Administração some (F58-S13).
//   - Cada card usa ADMIN_ACCESS (app/navigation.ts) — o MESMO valor da rota em App.tsx.
//
// Layout: abas (Conta · Administração) em desktop; select em mobile.
// DS: elev-2 (cards), Lift hover, tokens canônicos. Light + dark.
//
// Permissões (verificadas no código-fonte de cada página):
//   - Produtos & Regras    : credit_products:read (Products.tsx L12)
//   - Cidades              : cities:manage (gestor_regional/agente não veem o card)
//   - Agentes              : agents:manage (Agents.tsx + backend agents/routes.ts)
//   - Usuários & Papéis    : users:manage (backend users/routes.ts + roles/routes.ts)
//   - Feature Flags        : flags:manage (FeatureFlags.tsx L14)
//   - Cobrança — Parcelas  : billing:read + flag billing.enabled (billing/routes.ts L69)
//   - Cobrança — Réguas    : billing:write + flag billing.enabled (billing/routes.ts L138)
//   - Cobrança — Jobs      : billing:read + flag billing.enabled (billing/routes.ts L173)
//   - Templates WhatsApp   : templates:read (templates/routes.ts L47)
//   - IA no funil          : ai_actions:read + flag internal_assistant.actions.enabled
//                            (ai-actions/routes.ts — F25-S06/S07)
//   - Respostas rápidas    : livechat:quick_reply:read + flag livechat.quick_replies.enabled
//                            (quick-replies/routes.ts — F28-S07)
//   - Tipos de documento   : documents:manage + flag documents.enabled (fail-closed —
//                            gate reaproveitado de canAccessDocumentTypes, DocumentTypesPage.tsx
//                            L55, F46-S12). A flag nasce OFF em produção (migration 0139):
//                            hoje o card fica invisível de propósito — não é bug (F46-S26).
//   - Atribuição de inboxes: livechat:inbox_assignment:manage (inbox-assignments.routes.ts
//                            L37 + migration 0137). Sem flag — o CRUD funciona antes de
//                            `livechat.inbox_scope.enabled` ligar, de propósito (F47-S08).
//   - Aceites de política  : policies:manage (PolicyAcceptancesPage.tsx — F51-S17).
//                            Sem flag — política de monitoramento é sempre ativa.
//   - Meu WhatsApp         : channel.self_pair + flag whatsapp_sync.pairing.enabled
//                            (MeuWhatsAppPage — F56-S03). Mesmo gate do item do menu e do
//                            convite; "Canais > Vendedores" segue sendo de quem gerencia.
// =============================================================================

import * as React from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { ADMIN_ACCESS } from '../../app/navigation';
import {
  canAccessDocumentTypes,
  DOCUMENTS_FLAG_KEY,
} from '../../features/admin/document-types/DocumentTypesPage';
import { useFeatureFlags } from '../../hooks/useFeatureFlag';
import { useAuth } from '../../lib/auth-store';
import { cn } from '../../lib/cn';
import { ContextualHelp } from '../help/contextual';
import { CONNECT_PROMPT_FLAG, CONNECT_PROMPT_PERMISSION } from '../meu-whatsapp/connectPrompt';

import { ContaSection } from './ContaSection';

// ─── Ícones SVG inline (24×24) ────────────────────────────────────────────────

function IconProdutos(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      className="w-6 h-6 shrink-0"
      aria-hidden="true"
    >
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M6 7V5a4 4 0 0 1 12 0v2" />
      <path d="M9 13h6" />
    </svg>
  );
}

function IconCidades(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      className="w-6 h-6 shrink-0"
      aria-hidden="true"
    >
      <path d="M3 6l7-3 4 3 7-3v15l-7 3-4-3-7 3V6Z" />
      <circle cx="12" cy="11" r="2.5" />
    </svg>
  );
}

function IconAgentes(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      className="w-6 h-6 shrink-0"
      aria-hidden="true"
    >
      <circle cx="12" cy="8" r="4" />
      <path d="M4 22c0-4 3.58-7 8-7s8 3 8 7" />
      <path d="M16 12l2 2 3-3" />
    </svg>
  );
}

function IconUsuarios(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      className="w-6 h-6 shrink-0"
      aria-hidden="true"
    >
      <path d="M17 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
      <path d="M7 11a3 3 0 1 1 0-6 3 3 0 0 1 0 6Z" />
      <path d="M1 20c0-3.5 2.69-6 6-6h2" />
      <path d="M13 20c0-3.5 2.69-6 6-6h-2c-3.31 0-6 2.5-6 6Z" />
      <path d="M13 20h6" />
    </svg>
  );
}

function IconFeatureFlags(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      className="w-6 h-6 shrink-0"
      aria-hidden="true"
    >
      <path d="M4 3v18" />
      <path d="M4 3h14l-3 5 3 5H4" />
    </svg>
  );
}

function IconFollowup(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      className="w-6 h-6 shrink-0"
      aria-hidden="true"
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      <path d="M8 10h.01M12 10h.01M16 10h.01" strokeLinecap="round" />
    </svg>
  );
}

function IconAgenteIA(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      className="w-6 h-6 shrink-0"
      aria-hidden="true"
    >
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <circle cx="12" cy="12" r="3" />
      <path d="M7 5V3M12 5V3M17 5V3" strokeLinecap="round" />
      <path d="M9 12h.01M15 12h.01" strokeLinecap="round" />
    </svg>
  );
}

// Cobrança — Parcelas: ícone de recibo / documento de pagamento
function IconBillingDues(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      className="w-6 h-6 shrink-0"
      aria-hidden="true"
    >
      <path d="M6 2h12a2 2 0 0 1 2 2v16l-3-2-2 2-2-2-2 2-2-2-3 2V4a2 2 0 0 1 2-2Z" />
      <path d="M9 7h6M9 11h6M9 15h4" strokeLinecap="round" />
    </svg>
  );
}

// Cobrança — Réguas: ícone de régua / calendário (regras de cobrança)
function IconBillingRules(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      className="w-6 h-6 shrink-0"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="18" height="17" rx="2" />
      <path d="M3 9h18" />
      <path d="M8 2v4M16 2v4" strokeLinecap="round" />
      <path d="M7 14h4M13 14h4M7 18h2" strokeLinecap="round" />
    </svg>
  );
}

// Cobrança — Jobs: ícone de relógio / agendamento
function IconBillingJobs(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      className="w-6 h-6 shrink-0"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Tutoriais em vídeo: play em círculo
function IconTutoriais(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      className="w-6 h-6 shrink-0"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M10 8.5l6 3.5-6 3.5V8.5Z" strokeLinejoin="round" />
    </svg>
  );
}

// Papéis & Permissões: escudo com grade de checkmark
function IconPapeis(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      className="w-6 h-6 shrink-0"
      aria-hidden="true"
    >
      <path d="M12 3l8 3v6c0 4.5-3.5 8-8 9-4.5-1-8-4.5-8-9V6l8-3Z" />
      <path d="M8 12l2.5 2.5L16 9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Canais de Mensagem: balão de chat com raio (conectividade)
function IconCanais(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      className="w-6 h-6 shrink-0"
      aria-hidden="true"
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      <path d="M8 9.5h4M8 12.5h7" strokeLinecap="round" />
      <circle cx="18" cy="5" r="3" fill="currentColor" stroke="none" className="text-verde" />
    </svg>
  );
}

// Templates WhatsApp: balão de fala com linhas de texto
function IconTemplates(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      className="w-6 h-6 shrink-0"
      aria-hidden="true"
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      <path d="M8 9h8M8 13h5" strokeLinecap="round" />
    </svg>
  );
}

// Respostas rápidas (F28-S07) — balão de chat com raio (atalho/agilidade).
function IconRespostasRapidas(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      className="w-6 h-6 shrink-0"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="18" height="13" rx="3" />
      <path d="M8 17l-1.5 3.5V17" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M13 7.5l-3 4h2.2l-1.2 3 3-4h-2.2l1.2-3Z" fill="currentColor" stroke="none" />
    </svg>
  );
}

// Regras de Notificação: sino com engrenagem
function IconNotificacoes(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      className="w-6 h-6 shrink-0"
      aria-hidden="true"
    >
      <path
        d="M15 17H20L18.6 15.6A1.926 1.926 0 0 1 18 14.4V11A6 6 0 0 0 7 7.3L7 14.4A1.926 1.926 0 0 1 6.4 15.6L5 17H10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M10 17v1a2 2 0 0 0 4 0v-1" strokeLinecap="round" />
      <circle cx="18" cy="6" r="3" stroke="currentColor" strokeWidth={1.5} />
      <path d="M17.5 5.5l1 1" strokeLinecap="round" />
    </svg>
  );
}

// IA no funil: escudo com seta de retorno (supervisão + reversão — F25-S07)
function IconAcoesIA(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      className="w-6 h-6 shrink-0"
      aria-hidden="true"
    >
      <path d="M12 3l8 3v6c0 4.5-3.5 8-8 9-4.5-1-8-4.5-8-9V6l8-3Z" />
      <path d="M9.5 12.5a2.5 2.5 0 1 0 .8-1.85" strokeLinecap="round" />
      <path d="M9.5 9.5v2h2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Comercial: telefone com relógio (janela de contato + tentativas — F34-S06)
function IconComercial(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      className="w-6 h-6 shrink-0"
      aria-hidden="true"
    >
      <path d="M6 3h4l1.5 4.5-2 1.5a12 12 0 0 0 5.5 5.5l1.5-2L21 14v4a2 2 0 0 1-2 2C10.5 20 4 13.5 4 5a2 2 0 0 1 2-2Z" />
      <circle cx="18" cy="6" r="3.5" />
      <path d="M18 4.5V6l1 .8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Advocacia: balança da justiça
function IconAdvocacia(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      className="w-6 h-6 shrink-0"
      aria-hidden="true"
    >
      <path d="M12 4v16" strokeLinecap="round" />
      <path d="M5 18h14" strokeLinecap="round" />
      <path d="M5 4h14" strokeLinecap="round" />
      <path d="M5 4l-3 7a4 4 0 0 0 6 0L5 4Z" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M19 4l-3 7a4 4 0 0 0 6 0L19 4Z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Tipos de documento (F46-S12/F46-S26): documento com selo de conferido.
function IconDocumentos(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      className="w-6 h-6 shrink-0"
      aria-hidden="true"
    >
      <path d="M7 3h7l4 4v14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
      <path d="M14 3v4h4" />
      <path d="M9 13.5l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Atribuição de inboxes (F47-S08/F46-S26): caixa de entrada com seta de roteamento.
function IconInboxAssignments(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      className="w-6 h-6 shrink-0"
      aria-hidden="true"
    >
      <path d="M3 12l3-8h12l3 8" />
      <path d="M3 12v6a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-6" />
      <path d="M3 12h5l1.5 2.5h5L16 12h5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Aceites de política (F51-S17): documento com checkmark de assinatura/ciência.
function IconPolicyAcceptances(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      className="w-6 h-6 shrink-0"
      aria-hidden="true"
    >
      <path d="M7 3h7l4 4v14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
      <path d="M14 3v4h4" />
      <path d="M8 17l2 2 4.5-4.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Meu WhatsApp (F56-S03): celular com sinal de conexão.
function IconMeuWhatsApp(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      className="w-6 h-6 shrink-0"
      aria-hidden="true"
    >
      <rect x="7" y="2.5" width="10" height="19" rx="2" />
      <path d="M11 18.5h2" strokeLinecap="round" />
      <path d="M9.5 8.5a3.5 3.5 0 0 1 5 0M10.8 10.4a1.7 1.7 0 0 1 2.4 0" strokeLinecap="round" />
    </svg>
  );
}

// ─── Tipos ────────────────────────────────────────────────────────────────────

type Tab = 'conta' | 'administracao';

interface ConfigCard {
  title: string;
  description: string;
  icon: React.ReactNode;
  href?: string;
}

interface ConfigGroup {
  heading: string;
  cards: ConfigCard[];
}

// ─── Card navegável (Administração) ──────────────────────────────────────────

function AdminCard({ card }: { card: ConfigCard }): React.JSX.Element {
  return (
    <Link
      to={card.href!}
      className={cn(
        'group flex flex-col gap-3 p-5 rounded-lg border border-border',
        // Hover Lift: translateY(-4px) + sobe elev-1 → elev-4 (DS §8)
        'transition-all duration-[250ms] ease-out',
        'hover:-translate-y-1 focus-visible:-translate-y-1',
        'focus-visible:outline-none focus-visible:ring-2',
        'focus-visible:ring-[rgba(27,58,140,0.2)]',
      )}
      style={{ background: 'var(--bg-elev-1)', boxShadow: 'var(--elev-2)' }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.boxShadow = 'var(--elev-4)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.boxShadow = 'var(--elev-2)';
      }}
      onFocus={(e) => {
        (e.currentTarget as HTMLElement).style.boxShadow = 'var(--elev-4)';
      }}
      onBlur={(e) => {
        (e.currentTarget as HTMLElement).style.boxShadow = 'var(--elev-2)';
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="flex items-center justify-center w-10 h-10 rounded-md shrink-0 transition-colors duration-[150ms]"
          style={{
            background: 'var(--surface-muted)',
            color: 'var(--brand-azul)',
          }}
          aria-hidden="true"
        >
          {card.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h3
              className="font-sans font-semibold text-ink truncate group-hover:text-azul transition-colors duration-[150ms]"
              style={{ fontSize: 'var(--text-sm)', letterSpacing: '-0.01em' }}
            >
              {card.title}
            </h3>
            {/* Seta chevron */}
            <svg
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
              className="w-4 h-4 shrink-0 text-ink-4 group-hover:text-azul group-hover:translate-x-0.5 transition-all duration-[150ms]"
              aria-hidden="true"
            >
              <path d="M6 4l4 4-4 4" />
            </svg>
          </div>
          <p
            className="mt-1 font-sans text-ink-3 leading-relaxed"
            style={{ fontSize: 'var(--text-xs)' }}
          >
            {card.description}
          </p>
        </div>
      </div>
    </Link>
  );
}

// ─── Seção Conta ──────────────────────────────────────────────────────────────
// Implementação funcional em ContaSection.tsx (F8-S09).
// Re-exportada aqui via import para manter a estrutura de composição da página.

// ─── Seção Administração ──────────────────────────────────────────────────────

/**
 * Grupos de cards de administração que o usuário pode abrir. Cada card usa o
 * mesmo valor de `ADMIN_ACCESS` da rota correspondente em App.tsx (F58-S13).
 * Lista vazia = a aba Administração nem aparece.
 */
function useAdminGroups(): ConfigGroup[] {
  const { hasPermission } = useAuth();
  const { flags } = useFeatureFlags();

  // Helper: flag ativa quando status === 'enabled' ou 'internal_only' (padrão Sidebar)
  const flagEnabled = (key: string): boolean => {
    const status = flags[key];
    return status === 'enabled' || status === 'internal_only';
  };

  // ── Grupo Gestão ─────────────────────────────────────────────────────────────
  // Produtos: credit_products:read — confirmado em Products.tsx L12
  // Cidades: sem gating na UI — Cities.tsx L14 ("backend valida admin:cities:write")
  //          → card sempre visível para qualquer usuário autenticado
  // Agentes: agents:manage — convenção canônica :manage (F8-S10)
  // Cobrança: billing:read|write + flag billing.enabled — confirmado em billing/routes.ts
  // Templates: templates:read — confirmado em templates/routes.ts L47
  const gestaoCards: ConfigCard[] = [
    ...(hasPermission(ADMIN_ACCESS.products)
      ? [
          {
            title: 'Produtos & Regras',
            description: 'Linhas de crédito, taxas, prazos e regras de publicação.',
            icon: <IconProdutos />,
            href: '/admin/products',
          },
        ]
      : []),
    // Cidades: gestão do catálogo de municípios — requer cities:manage (admin).
    // Sem o gating, gestor_regional/agente viam o card e a página dava 403.
    ...(hasPermission(ADMIN_ACCESS.cities)
      ? [
          {
            title: 'Cidades',
            description: 'Municípios atendidos, IBGE codes e cobertura do Banco do Povo.',
            icon: <IconCidades />,
            href: '/admin/cities',
          },
        ]
      : []),
    ...(hasPermission(ADMIN_ACCESS.agents)
      ? [
          {
            title: 'Agentes',
            description: 'Agentes de crédito, vínculos com cidades e perfis de acesso.',
            icon: <IconAgentes />,
            href: '/admin/agents',
          },
        ]
      : []),
    // Follow-up — Réguas: gated por followup:write (admin + gestor_geral)
    ...(hasPermission(ADMIN_ACCESS.followupRules)
      ? [
          {
            title: 'Follow-up — Réguas',
            description: 'Configure quando e como contatar leads inativos automaticamente.',
            icon: <IconFollowup />,
            href: '/admin/followup/rules',
          },
        ]
      : []),
    // Follow-up — Jobs: gated por followup:read (admin + gestor_geral + gestor_regional)
    ...(hasPermission(ADMIN_ACCESS.followupJobs)
      ? [
          {
            title: 'Follow-up — Jobs',
            description: 'Monitore e gerencie envios de follow-up agendados.',
            icon: <IconFollowup />,
            href: '/admin/followup/jobs',
          },
        ]
      : []),
    // Cobrança — Parcelas: billing:read + flag billing.enabled
    ...(hasPermission(ADMIN_ACCESS.billingDues) && flagEnabled('billing.enabled')
      ? [
          {
            title: 'Cobrança — Parcelas',
            description: 'Visualize e gerencie parcelas de contratos em cobrança.',
            icon: <IconBillingDues />,
            href: '/admin/billing/dues',
          },
        ]
      : []),
    // Cobrança — Réguas: billing:write + flag billing.enabled
    ...(hasPermission(ADMIN_ACCESS.billingRules) && flagEnabled('billing.enabled')
      ? [
          {
            title: 'Cobrança — Réguas',
            description: 'Configure as réguas de cobrança: prazos, canais e escalonamento.',
            icon: <IconBillingRules />,
            href: '/admin/billing/rules',
          },
        ]
      : []),
    // Cobrança — Jobs: billing:read + flag billing.enabled
    ...(hasPermission(ADMIN_ACCESS.billingJobs) && flagEnabled('billing.enabled')
      ? [
          {
            title: 'Cobrança — Jobs',
            description: 'Monitore execuções agendadas do motor de cobrança.',
            icon: <IconBillingJobs />,
            href: '/admin/billing/jobs',
          },
        ]
      : []),
    // Templates WhatsApp: templates:read (sem flag — sempre disponível quando autorizado)
    ...(hasPermission(ADMIN_ACCESS.templates)
      ? [
          {
            title: 'Templates WhatsApp',
            description: 'Gerencie modelos de mensagem aprovados pelo Meta para envios em massa.',
            icon: <IconTemplates />,
            href: '/admin/templates',
          },
        ]
      : []),
    // Respostas rápidas: livechat:quick_reply:read + flag livechat.quick_replies.enabled
    // (F28-S07). read cobre admin/gestor_geral/agente — quem só tem `write` gerencia
    // apenas as próprias; a opção "Organização" exige `manage` (gating dentro da tela).
    ...(hasPermission(ADMIN_ACCESS.quickReplies) && flagEnabled('livechat.quick_replies.enabled')
      ? [
          {
            title: 'Respostas rápidas',
            description:
              'Biblioteca de modelos de mensagem para agilizar o atendimento no WhatsApp.',
            icon: <IconRespostasRapidas />,
            href: '/admin/quick-replies',
          },
        ]
      : []),
    // Comercial: gated por sales_campaigns:manage (F34-S06, doc 26 §6.9)
    ...(hasPermission(ADMIN_ACCESS.commercial)
      ? [
          {
            title: 'Comercial',
            description: 'Fuso, janelas de contato, desfechos, tentativas e distribuição.',
            icon: <IconComercial />,
            href: '/configuracoes/comercial',
          },
        ]
      : []),
    // Advocacia: gated por law_firms:manage (admin + gestor_geral)
    ...(hasPermission(ADMIN_ACCESS.lawFirms)
      ? [
          {
            title: 'Advocacia',
            description:
              'Escritórios de advocacia parceiros para encaminhamento de processos por cidade.',
            icon: <IconAdvocacia />,
            href: '/configuracoes/advocacia',
          },
        ]
      : []),
    // Canais de Mensagem: gated por channel.connect
    ...(hasPermission(ADMIN_ACCESS.channels)
      ? [
          {
            title: 'Canais de Mensagem',
            description: 'Conecte o WhatsApp Business para receber e enviar mensagens no inbox.',
            icon: <IconCanais />,
            href: '/admin/canais',
          },
        ]
      : []),
    // Tipos de documento: mesmo gate fail-closed da própria tela (canAccessDocumentTypes,
    // DocumentTypesPage.tsx) — documents:manage E flag documents.enabled. A flag nasce OFF
    // em produção (migration 0139): o card fica invisível hoje de propósito, não é bug.
    ...(canAccessDocumentTypes(hasPermission(ADMIN_ACCESS.documentTypes), flags[DOCUMENTS_FLAG_KEY])
      ? [
          {
            title: 'Tipos de documento',
            description: 'Catálogo de documentos exigidos em análises e contratos.',
            icon: <IconDocumentos />,
            href: '/admin/document-types',
          },
        ]
      : []),
    // Atribuição de inboxes: livechat:inbox_assignment:manage (inbox-assignments.routes.ts
    // L37 + migration 0137). Sem flag — o CRUD funciona antes de
    // `livechat.inbox_scope.enabled` ligar, de propósito (F47-S08).
    ...(hasPermission(ADMIN_ACCESS.inboxAssignments)
      ? [
          {
            title: 'Atribuição de inboxes',
            description: 'Diga quem responde por qual caixa do live chat (fora da WAHA pessoal).',
            icon: <IconInboxAssignments />,
            href: '/admin/inbox-assignments',
          },
        ]
      : []),
    // Agente de IA — Prompts: gated por ai_prompts:read (admin + gestor_geral)
    ...(hasPermission(ADMIN_ACCESS.aiPrompts)
      ? [
          {
            title: 'Agente de IA — Prompts',
            description:
              'Gerencie os prompts do agente de IA e controle versões ativas em produção.',
            icon: <IconAgenteIA />,
            href: '/configuracoes/ia/prompts',
          },
        ]
      : []),
    // Agente de IA — Decisões: gated por ai_decisions:read (admin + gestor_geral + gestor_regional)
    ...(hasPermission(ADMIN_ACCESS.aiDecisions)
      ? [
          {
            title: 'Agente de IA — Decisões',
            description:
              'Visualize decisões do agente conversa a conversa: intent, prompt, modelo, tokens e custo.',
            icon: <IconAgenteIA />,
            href: '/configuracoes/ia/decisoes',
          },
        ]
      : []),
    // Agente de IA — Playground: gated por ai_playground:run (admin)
    ...(hasPermission(ADMIN_ACCESS.aiPlayground)
      ? [
          {
            title: 'Agente de IA — Playground',
            description:
              'Teste o grafo do agente em modo dry-run sem persistir nem enviar ao cliente.',
            icon: <IconAgenteIA />,
            href: '/configuracoes/ia/playground',
          },
        ]
      : []),
    // IA no funil: ai_actions:read + flag internal_assistant.actions.enabled (F25-S07)
    // Mesmo padrão AND-gate de billing/tutorials — card só aparece com os dois
    // habilitados; a rota em si (acesso direto) permanece só permissão-gated.
    ...(hasPermission(ADMIN_ACCESS.aiActions) && flagEnabled('internal_assistant.actions.enabled')
      ? [
          {
            title: 'IA no funil',
            description:
              'Ações autônomas do agente de IA no funil nas últimas 24h — qualificações, ' +
              'estagnações e abandonos, com opção de reverter.',
            icon: <IconAcoesIA />,
            href: '/configuracoes/ia/acoes',
          },
        ]
      : []),
  ];

  // ── Grupo Administração técnica ───────────────────────────────────────────────
  // Usuários: users:manage — convenção canônica :manage (F8-S10)
  // Feature Flags: flags:manage — confirmado em FeatureFlags.tsx L14
  const tecnicaCards: ConfigCard[] = [
    ...(hasPermission(ADMIN_ACCESS.users)
      ? [
          {
            title: 'Usuários & Papéis',
            description: 'Contas de usuário, papéis RBAC e controle de acesso.',
            icon: <IconUsuarios />,
            href: '/admin/users',
          },
        ]
      : []),
    // Papéis & Permissões: matrix de permissões por papel — users:assign_privileged_roles
    ...(hasPermission(ADMIN_ACCESS.roles)
      ? [
          {
            title: 'Papéis & Permissões',
            description: 'Defina quais permissões cada papel possui na plataforma.',
            icon: <IconPapeis />,
            href: '/admin/papeis',
          },
        ]
      : []),
    ...(hasPermission(ADMIN_ACCESS.featureFlags)
      ? [
          {
            title: 'Feature Flags',
            description: 'Ativar e desativar funcionalidades da plataforma em tempo real.',
            icon: <IconFeatureFlags />,
            href: '/admin/feature-flags',
          },
        ]
      : []),
    // Tutoriais em vídeo: tutorials:manage + flag tutorials.enabled (F12-S10)
    ...(hasPermission(ADMIN_ACCESS.tutorials) && flagEnabled('tutorials.enabled')
      ? [
          {
            title: 'Tutoriais em vídeo',
            description: 'Gerencie tutoriais em vídeo exibidos na Central de Ajuda da plataforma.',
            icon: <IconTutoriais />,
            href: '/admin/tutoriais',
          },
        ]
      : []),
    // Regras de Notificação: notifications:manage (F24-S10)
    ...(hasPermission(ADMIN_ACCESS.notifications)
      ? [
          {
            title: 'Regras de Notificação',
            description:
              'Configure quando e como a plataforma notifica agentes e gestores sobre eventos operacionais.',
            icon: <IconNotificacoes />,
            href: '/admin/notificacoes',
          },
        ]
      : []),
    // Aceites de política: policies:manage (F51-S17) — a própria página reaplica o
    // gate (PolicyAcceptancesPage.tsx); o card só some da lista, não é a defesa real.
    ...(hasPermission(ADMIN_ACCESS.policies)
      ? [
          {
            title: 'Aceites de política',
            description:
              'Relação de quem confirmou ciência da política de monitoramento da linha ' +
              'corporativa, com exportação em CSV.',
            icon: <IconPolicyAcceptances />,
            href: '/admin/policies/acceptances',
          },
        ]
      : []),
  ];

  // ── Grupo Meu atendimento (F56-S03) ───────────────────────────────────────────
  // Entrada para o vendedor conectar o próprio celular. Mesmo gate do item do
  // menu e do convite: permissão channel.self_pair E flag whatsapp_sync.pairing.enabled.
  const meuAtendimentoCards: ConfigCard[] =
    hasPermission(CONNECT_PROMPT_PERMISSION) && flagEnabled(CONNECT_PROMPT_FLAG)
      ? [
          {
            title: 'Meu WhatsApp',
            description:
              'Conecte o WhatsApp do seu celular para ver e responder suas conversas aqui.',
            icon: <IconMeuWhatsApp />,
            href: '/meu-whatsapp',
          },
        ]
      : [];

  return [
    ...(meuAtendimentoCards.length > 0
      ? [{ heading: 'Meu atendimento', cards: meuAtendimentoCards }]
      : []),
    ...(gestaoCards.length > 0 ? [{ heading: 'Gestão', cards: gestaoCards }] : []),
    ...(tecnicaCards.length > 0 ? [{ heading: 'Administração técnica', cards: tecnicaCards }] : []),
  ];
}

function AdminSection({ grupos }: { grupos: ConfigGroup[] }): React.JSX.Element {
  return (
    <div className="flex flex-col gap-8">
      {grupos.map((grupo) => (
        <div key={grupo.heading} className="flex flex-col gap-3">
          <h2
            className="font-sans font-semibold uppercase tracking-widest text-ink-3"
            style={{ fontSize: '0.7rem', letterSpacing: '0.12em' }}
          >
            {grupo.heading}
          </h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {grupo.cards.map((card) => (
              <AdminCard key={card.title} card={card} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Tabs de navegação ────────────────────────────────────────────────────────

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

function TabButton({ active, onClick, children }: TabButtonProps): React.JSX.Element {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'relative pb-3 font-sans text-sm font-medium transition-colors duration-[150ms]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(27,58,140,0.2)] rounded-sm',
        active ? 'text-azul' : 'text-ink-3 hover:text-ink',
      )}
    >
      {children}
      {/* Indicador ativo */}
      {active && (
        <span
          className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full"
          style={{ background: 'var(--brand-azul)' }}
          aria-hidden="true"
        />
      )}
    </button>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────

/**
 * Hub de configurações `/configuracoes`.
 *
 * Duas abas:
 *   - Conta: perfil, segurança, aparência (funcional — F8-S09).
 *   - Administração: cards gated por permissão, subdivididos em Gestão e Adm. técnica.
 */
export function ConfiguracoesPage(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();

  // Aba ativa via query param ?tab= (preserva deep link / histórico)
  // A aba Administração só existe se ao menos um card é permitido (F58-S13): sem
  // nenhum, ela era um beco sem saída. ?tab=administracao sem acesso cai em Conta.
  const adminGroups = useAdminGroups();
  const hasAdminTab = adminGroups.length > 0;
  const tabParam = searchParams.get('tab');
  const activeTab: Tab = tabParam === 'administracao' && hasAdminTab ? 'administracao' : 'conta';

  const setTab = React.useCallback(
    (tab: Tab) => {
      setSearchParams(tab === 'conta' ? {} : { tab }, { replace: true });
    },
    [setSearchParams],
  );

  return (
    <div className="flex flex-col gap-6 pb-12">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div>
        <div className="flex items-center gap-1">
          <h1
            className="font-display font-bold text-ink"
            style={{
              fontSize: 'var(--text-3xl)',
              letterSpacing: '-0.04em',
              lineHeight: '1',
              fontVariationSettings: "'opsz' 32",
            }}
          >
            Configurações
          </h1>
          {/* ⓘ tutorial de configurações — norma 21 §7 */}
          <ContextualHelp
            featureKey="settings.organization.edit"
            permission="users:manage"
            className="ml-0.5"
          />
        </div>
        <p className="mt-1.5 font-sans text-ink-3" style={{ fontSize: 'var(--text-sm)' }}>
          Gerencie sua conta e as configurações administrativas da plataforma.
        </p>
      </div>

      {/* ── Tabs (desktop) / Select (mobile) ────────────────────────────── */}
      {/* Desktop: tabs horizontais com indicador de linha */}
      <div
        className="hidden sm:flex gap-6 border-b border-border"
        role="tablist"
        aria-label="Seções de configurações"
      >
        <TabButton active={activeTab === 'conta'} onClick={() => setTab('conta')}>
          Conta
        </TabButton>
        {hasAdminTab && (
          <TabButton active={activeTab === 'administracao'} onClick={() => setTab('administracao')}>
            Administração
          </TabButton>
        )}
      </div>

      {/* Mobile: select nativo */}
      <div className="sm:hidden">
        <select
          value={activeTab}
          onChange={(e) => setTab(e.target.value as Tab)}
          className={cn(
            'w-full rounded-md border border-border px-3 py-2',
            'font-sans text-sm text-ink bg-surface-1',
            'focus:outline-none focus:ring-2 focus:ring-[rgba(27,58,140,0.2)]',
          )}
          style={{ boxShadow: 'var(--elev-1)' }}
          aria-label="Navegar entre seções"
        >
          <option value="conta">Conta</option>
          {hasAdminTab && <option value="administracao">Administração</option>}
        </select>
      </div>

      {/* ── Conteúdo da aba ─────────────────────────────────────────────── */}
      <div role="tabpanel" aria-label={activeTab === 'conta' ? 'Conta' : 'Administração'}>
        {activeTab === 'conta' ? <ContaSection /> : <AdminSection grupos={adminGroups} />}
      </div>
    </div>
  );
}
